import { useEffect, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createOperario,
  deleteOperario,
  listOperarios,
  updateOperario,
  type OperarioItem,
} from "@/lib/api";

const LEVELS = [
  { value: "tecnico", label: "Técnico" },
  { value: "senior", label: "Senior" },
  { value: "jefe", label: "Jefe de terreno" },
];

const levelLabel = (l: string) => LEVELS.find((x) => x.value === l)?.label ?? l;

type FormState = { name: string; rut: string; level: string; phone: string };
const emptyForm: FormState = { name: "", rut: "", level: "tecnico", phone: "" };

export default function OperariosTab() {
  const { session } = useSession();
  const [items, setItems] = useState<OperarioItem[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError(null);
      try {
        const rows = await listOperarios(session);
        if (!cancelled) setItems(rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar operarios");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [session]);

  function openCreate(): void {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setShowModal(true);
  }

  function openEdit(item: OperarioItem): void {
    setEditingId(item.id);
    setForm({ name: item.name, rut: item.rut ?? "", level: item.level, phone: item.phone ?? "" });
    setError(null);
    setShowModal(true);
  }

  function closeModal(): void {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
  }

  async function onSave(): Promise<void> {
    if (!session || !form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        rut: form.rut.trim() || undefined,
        level: form.level,
        phone: form.phone.trim() || undefined,
      };
      if (editingId) {
        const updated = await updateOperario(session, editingId, payload);
        setItems((prev) => prev.map((i) => (i.id === editingId ? updated : i)));
      } else {
        const created = await createOperario(session, payload);
        setItems((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, "es")));
      }
      closeModal();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  async function onToggleActive(item: OperarioItem): Promise<void> {
    if (!session) return;
    try {
      const updated = await updateOperario(session, item.id, { active: !item.active });
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo actualizar");
    }
  }

  async function onDelete(item: OperarioItem): Promise<void> {
    if (!session || !window.confirm(`¿Eliminar operario "${item.name}"?`)) return;
    setError(null);
    try {
      await deleteOperario(session, item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo eliminar");
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">Operarios ({items.length})</h2>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <i className="ri-add-line" />
            Nuevo operario
          </button>
        </div>

        {loading ? (
          <div className="h-16 bg-gray-100 rounded animate-pulse" />
        ) : (
          <div className="space-y-2">
            {items.length === 0 ? (
              <p className="text-sm text-gray-500">Sin operarios registrados.</p>
            ) : (
              items.map((item) => (
                <div key={item.id} className={`flex items-center justify-between border rounded-lg px-3 py-2 ${item.active ? "border-gray-100" : "border-gray-100 bg-gray-50 opacity-60"}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{item.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        item.level === "jefe" ? "bg-purple-100 text-purple-700" :
                        item.level === "senior" ? "bg-blue-100 text-blue-700" :
                        "bg-gray-100 text-gray-600"
                      }`}>{levelLabel(item.level)}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {item.rut && <span className="mr-2">RUT {item.rut}</span>}
                      {item.phone && <span>{item.phone}</span>}
                      {!item.active && <span className="ml-2 text-orange-500">· Inactivo</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => void onToggleActive(item)}
                      className={`text-xs px-2 py-1 border rounded hover:opacity-80 ${item.active ? "border-gray-200 text-gray-500" : "border-green-200 text-green-600"}`}
                    >
                      {item.active ? "Desactivar" : "Activar"}
                    </button>
                    <button onClick={() => openEdit(item)} className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50">
                      Editar
                    </button>
                    <button
                      onClick={() => void onDelete(item)}
                      className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">{editingId ? "Editar operario" : "Nuevo operario"}</h3>
              <button onClick={closeModal} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nombre completo</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Ej: Juan Pérez González"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nivel</label>
                <select
                  value={form.level}
                  onChange={(e) => setForm((p) => ({ ...p, level: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                >
                  {LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">RUT <span className="font-normal text-gray-400">(opcional)</span></label>
                <input
                  value={form.rut}
                  onChange={(e) => setForm((p) => ({ ...p, rut: e.target.value }))}
                  placeholder="Ej: 12.345.678-9"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono focus:outline-none focus:border-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Teléfono <span className="font-normal text-gray-400">(opcional)</span></label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="+56 9 1234 5678"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                />
              </div>

              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={closeModal} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={() => void onSave()}
                disabled={saving || !form.name.trim()}
                className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-60 hover:bg-blue-700"
              >
                {saving ? "Guardando..." : editingId ? "Actualizar" : "Crear operario"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
